import React, { useMemo } from 'react';

import {
  IsoTimestamp,
  assertIsoTimestamp,
  formatDate,
  formatTimeRange,
  getTZAbbr,
  startOfDay,
} from '@modernloop/shared/datetime';
import { Typography, Tooltip, Box } from '@mui/material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';

import { SelfScheduleTimeRange } from './useLoadSelfScheduleOptionsByTimeRange';

type Props = {
  pickerDayProps: PickersDayProps<IsoTimestamp>;
  options: number;
  timeRanges?: SelfScheduleTimeRange[];
  selected: boolean;
  timezone: string;
};

const OptionDay = ({ pickerDayProps, options, timeRanges, selected, timezone }: Props) => {
  const { day } = pickerDayProps;

  const tooltipContent = useMemo(() => {
    if (!options || !timeRanges?.length) {
      return null;
    }

    const validatedRanges = timeRanges
      .filter((range) => startOfDay(assertIsoTimestamp(range.startAt), timezone) === day)
      .sort((first, second) => first.startAt.localeCompare(second.startAt));

    if (!validatedRanges.length) {
      return null;
    }

    const dateHeader = `${formatDate(day, timezone, 'EEEE, MMMM d, yyyy')} · ${getTZAbbr(timezone)}`;

    return (
      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {dateHeader}
        </Typography>
        {validatedRanges.map((range) => (
          <Typography key={`${range.startAt}-${range.endAt}`} variant="body2">
            {formatTimeRange(assertIsoTimestamp(range.startAt), assertIsoTimestamp(range.endAt), timezone, true)}
          </Typography>
        ))}
      </Box>
    );
  }, [day, options, timeRanges, timezone]);

  const dayContent = (
    <PickersDay
      {...pickerDayProps}
      data-testid="self-schedule-day-option-container"
      selected={selected}
      color="transparent"
      sx={[
        {
          borderRadius: '6px',
          width: '44px',
          height: '44px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          border: (theme) => {
            return !selected ? `1px solid ${theme.palette.borderState.default}` : undefined;
          },
          overflow: 'hidden',
          margin: '0 auto',
          marginBottom: '4px',
        },
        selected && {
          border: '1px solid',
          backgroundColor: (theme) => `${theme.palette.info.tint} !important`,
        },
      ]}
    >
      <Typography
        variant="body2"
        data-testid="self-schedule-option-day"
        fontWeight={selected ? 600 : 500}
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          color: (theme) => (selected ? theme.palette.primary.main : theme.palette.text.primary),
          backgroundColor: (theme) => (selected ? theme.palette.info.tint : theme.palette.background.default),
        }}
      >
        <span style={{ textAlign: 'center', width: '100%' }}>{formatDate(day, timezone, 'd')}</span>
      </Typography>

      <Typography
        variant="body2"
        data-testid="self-schedule-option-count"
        fontWeight={500}
        sx={[
          !selected && {
            width: '100%',
            color: (theme) => theme.palette.text.primary,
            backgroundColor: (theme) => theme.palette.background.contrast,
          },
          selected &&
            options > 0 && {
              width: '100%',
              backgroundColor: (theme) => theme.palette.primary.light,
              color: (theme) => theme.palette.secondary.light,
            },
          selected &&
            options === 0 && {
              width: '100%',
              borderTop: (theme) => `1px solid ${theme.palette.borderState.default}`,
              backgroundColor: (theme) => theme.palette.primary.tint,
              color: (theme) => theme.palette.text.secondary,
            },
        ]}
      >
        {options}
      </Typography>
    </PickersDay>
  );

  if (!tooltipContent) {
    return dayContent;
  }

  return (
    <Tooltip title={tooltipContent}>
      <Box component="span" sx={{ display: 'flex', flexGrow: 1 }}>
        {dayContent}
      </Box>
    </Tooltip>
  );
};

export default OptionDay;
